"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { ChevronUp, ChevronDown } from "lucide-react";

const CONSENT_STORAGE_KEY = "cookie-consent";

type OptionalConsent = {
  analytics: boolean;
  marketing: boolean;
};

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [optionalConsent, setOptionalConsent] = useState<OptionalConsent>({
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) {
      setVisible(true);
      return;
    }

    try {
      const consent = JSON.parse(stored);
      if (!consent.necessary) {
        setVisible(true);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const saveConsent = (consent: OptionalConsent) => {
    localStorage.setItem(
      CONSENT_STORAGE_KEY,
      JSON.stringify({ necessary: true, ...consent }),
    );
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-150 ease-out ${
          settingsOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        aria-hidden="true"
      />
      <dialog
        open
        aria-label="Cookie-Hinweis"
        className="m-0 max-w-none overflow-visible border-0 bg-transparent p-4 fixed inset-x-0 bottom-0 z-50 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-md"
      >
        <div className="rounded-lg bg-[oklch(0.28_0_0)] text-white p-6 shadow-[0_10px_30px_-5px_rgba(0,0,0,0.9)] space-y-4">
          {!settingsOpen && (
            <p className="text-sm">
              Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf
              sprängi.ch zu bieten. Notwendige Cookies sind immer aktiv. Mit
              Ihrer Einwilligung nutzen wir optionale Cookies für Analyse und
              Marketing.{" "}
              <Link href="/agb" className="underline hover:no-underline">
                Datenschutzerklärung
              </Link>
            </p>
          )}

          {settingsOpen && (
            <div className="space-y-4">
              <label className="flex items-start gap-3 rounded-lg border border-white/20 bg-white/5 p-3 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 accent-primary"
                  checked
                  disabled
                />
                <span>
                  <span className="mb-1 flex items-center gap-2 font-medium">
                    Notwendig{" "}
                    <span className="inline-flex h-5 items-center rounded-full bg-primary px-2 text-[10px] leading-[1] font-normal uppercase text-primary-foreground">
                      Immer aktiv
                    </span>
                  </span>{" "}
                  Für den Betrieb der Website zwingend erforderlich
                  (Authentifizierung, Sicherheit, Session-Management). Diese
                  Cookies können nicht deaktiviert werden.
                </span>
              </label>

              <label
                className={`flex items-start gap-3 rounded-lg border border-white/20 p-3 text-sm cursor-pointer ${
                  optionalConsent.analytics ? "bg-white/5" : ""
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-1 accent-primary cursor-pointer"
                  checked={optionalConsent.analytics}
                  onChange={(e) =>
                    setOptionalConsent((prev) => ({
                      ...prev,
                      analytics: e.target.checked,
                    }))
                  }
                />
                <span>
                  <span className="mb-1 block font-medium">
                    Analyse & Performance
                  </span>{" "}
                  Helfen uns zu verstehen, wie Besucher unsere Website nutzen
                  (z. B. meistbesuchte Seiten, Verweildauer). Die Daten werden
                  anonymisiert ausgewertet.
                </span>
              </label>

              <label
                className={`flex items-start gap-3 rounded-lg border border-white/20 p-3 text-sm cursor-pointer ${
                  optionalConsent.marketing ? "bg-white/5" : ""
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-1 accent-primary cursor-pointer"
                  checked={optionalConsent.marketing}
                  onChange={(e) =>
                    setOptionalConsent((prev) => ({
                      ...prev,
                      marketing: e.target.checked,
                    }))
                  }
                />
                <span>
                  <span className="mb-1 block font-medium">
                    Marketing & Werbung
                  </span>{" "}
                  Ermöglichen personalisierte Werbung und das Messen der
                  Wirksamkeit von Kampagnen auf externen Plattformen.
                </span>
              </label>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            {settingsOpen ? (
              <>
                <Button
                  variant="default"
                  className="rounded-full uppercase cursor-pointer w-full sm:flex-1"
                  onClick={() => saveConsent(optionalConsent)}
                >
                  Auswahl bestätigen
                </Button>
                <Button
                  variant="secondaryLight"
                  className="rounded-full uppercase cursor-pointer w-full sm:flex-1"
                  onClick={() =>
                    saveConsent({ analytics: true, marketing: true })
                  }
                >
                  Alle akzeptieren
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="default"
                  className="rounded-full uppercase cursor-pointer w-full sm:flex-1"
                  onClick={() =>
                    saveConsent({ analytics: true, marketing: true })
                  }
                >
                  Akzeptieren
                </Button>
                <Button
                  variant="secondaryLight"
                  className="rounded-full uppercase cursor-pointer w-full sm:flex-1"
                  onClick={() =>
                    saveConsent({ analytics: false, marketing: false })
                  }
                >
                  Ablehnen
                </Button>
              </>
            )}
          </div>

          {settingsOpen && (
            <button
              type="button"
              className="mx-auto block w-fit text-sm hover:underline cursor-pointer"
              onClick={() =>
                saveConsent({ analytics: false, marketing: false })
              }
            >
              Nur Notwendige
            </button>
          )}

          <button
            type="button"
            aria-expanded={settingsOpen}
            className={`relative mx-auto flex w-fit items-center text-sm hover:underline cursor-pointer ${
              settingsOpen ? "mt-6" : ""
            }`}
            onClick={() => setSettingsOpen((prev) => !prev)}
          >
            {settingsOpen
              ? "Einstellungen schliessen"
              : "Einstellungen anpassen"}
            {settingsOpen ? (
              <ChevronDown className="absolute left-full ml-1.5 size-4" />
            ) : (
              <ChevronUp className="absolute left-full ml-1.5 size-4" />
            )}
          </button>
        </div>
      </dialog>
    </>
  );
}
